import { ActionPlanSummary, RiskAssessment } from "@mutiny-group/sbo-openapi-client";
import { useQuery } from "@tanstack/react-query";

import { components } from "@/@types/api";
import { INSIGHTS_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import { axiosApi } from "@/Utils";

type BaseScenario = components["schemas"]["NewScenarioV2"];
export type Scenario = BaseScenario & {
  isEligibleForActionPlans?: boolean;
  risk?: RiskAssessment;
  eligibleActionPlans?: ActionPlanSummary[];
  approvedActionPlans?: ActionPlanSummary[];
};
type ScenarioRevenue = components["schemas"]["ScenarioResultRevenue"];
type ScenarioSpend = components["schemas"]["ScenarioResultSpend"];
type ScenarioReport = components["schemas"]["NewScenarioReportV2"];

type NodeDTO = components["schemas"]["NodeDTO"];

export const getScenario = ({
  scenarioUUID,
  breakdownResultsGeography,
}: {
  scenarioUUID: string;
  breakdownResultsGeography?: boolean;
}): Promise<Scenario> => {
  const params = breakdownResultsGeography ? { breakdownResultsGeography: "true" } : {};

  return axiosApi.get(`/api/v2/scenarios-new/v2/${scenarioUUID}`, {
    baseURL: INSIGHTS_BACKEND_URL,
    params,
  });
};

type QueryFnType = typeof getScenario;

type UseScenarioOptions = {
  scenarioUUID: string;
  breakdownResultsGeography?: boolean;
  config?: QueryConfig<QueryFnType>;
};

export const useScenario = ({
  scenarioUUID,
  breakdownResultsGeography,
  config,
}: UseScenarioOptions) => {
  return useQuery({
    ...config,
    queryKey: ["scenario", scenarioUUID, breakdownResultsGeography],
    queryFn: () => getScenario({ scenarioUUID, breakdownResultsGeography }),
    placeholderData: placeholderScenario,
  });
};

const placeholderScenario: Scenario = {
  uuid: "",
  name: "",
  description: "",
  value: null,
  startDate: null,
  endDate: null,
  scenarioType: "unknown",
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
  createdBy: "",
  scenarioStatus: "unknown",
  statusMessage: "",
  targetNode: {} as NodeDTO,
  scenarioConstraints: [],
  geography: null,
  scenarioReport: {
    uuid: "",
    scenarioUuid: "",
    createdAt: "",
    updatedAt: "",
    confidence: null,
    scenario: {} as Scenario,
    scenarioCoVarianceMatrices: [],
    scenarioRevenues: [] as ScenarioRevenue[],
    scenarioSpends: [] as ScenarioSpend[],
  } as ScenarioReport,
};
